import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';

const ANY_VALUE = '__ANY__';

type Option<T extends string> = {
  value: T | '';
  labelKey: string;
};

type Props<S extends string, P extends string, M extends string> = {
  statusValue: S | null;
  statusOptions: Option<S>[];
  onStatusChange: (value: S | null) => void;
  /** Slot 2 — geralmente provider (payments) ou reason (refunds). */
  secondaryValue?: P | null;
  secondaryOptions?: Option<P>[];
  onSecondaryChange?: (value: P | null) => void;
  secondaryLabel?: string;
  /** Slot 3 — método de pagamento (só payments). */
  methodValue?: M | null;
  methodOptions?: Option<M>[];
  onMethodChange?: (value: M | null) => void;
  /** Texto livre — busca por tenantId (client-side). */
  searchValue: string;
  onSearchChange: (value: string) => void;
};

/**
 * Barra de filtros (busca por tenantId + status + slot secundário + método) reutilizada por
 * `/finance/transactions` e `/finance/refunds`.
 *
 * Origem: sem issue — commit 973be8d9c, 2026-05-05 12:08 +0200 (Sprint 5 PR H, painel
 * financeiro).
 *
 * Por quê: os selects de status/provider/método são MUTUAMENTE EXCLUSIVOS porque as queries
 * cross-tenant dessas telas usam índices de um filtro de igualdade + ordenação (`payments`:
 * `status|provider|payment_method_type + created_at`; `refunds`: `status|reason +
 * requested_at`, em `firebase/firestore.indexes.json`). Combinar dois exigiria índice novo; a
 * UI limpa os outros ao trocar um (`clearOthers`). A busca por texto é client-side.
 *
 * Contrato: genérico em `S`/`P`/`M` (enums das telas); `''`/`__ANY__` = sem filtro, entregue
 * ao chamador como `null`. Só apresentação e callbacks; não consulta nada.
 *
 * Usado por: `routes/TransactionsPage.tsx`, `routes/RefundsPage.tsx` (web `apps/admin`).
 *
 * Histórico:
 *  - 2026-06-08 22:54 +0200 — (39-F) shadcn → Cloudscape `Select`/`Input`.
 */
export function FinanceFilters<S extends string, P extends string, M extends string>({
  statusValue,
  statusOptions,
  onStatusChange,
  secondaryValue = null,
  secondaryOptions,
  onSecondaryChange,
  secondaryLabel,
  methodValue = null,
  methodOptions,
  onMethodChange,
  searchValue,
  onSearchChange,
}: Props<S, P, M>): React.JSX.Element {
  const { t } = useTranslation();

  /** Zera os outros selects — um filtro de igualdade por vez, por causa dos índices. */
  function clearOthers(except: 'status' | 'secondary' | 'method'): void {
    if (except !== 'status' && statusValue !== null) onStatusChange(null);
    if (except !== 'secondary' && secondaryValue !== null) onSecondaryChange?.(null);
    if (except !== 'method' && methodValue !== null) onMethodChange?.(null);
  }

  return (
    <SpaceBetween direction="horizontal" size="s">
      <FormField label={t('admin.finance.filters.search')}>
        <Input
          type="search"
          value={searchValue}
          onChange={({ detail }): void => {
            onSearchChange(detail.value);
          }}
          placeholder={t('admin.finance.filters.searchPlaceholder')}
        />
      </FormField>
      <SelectFilter
        label={t('admin.finance.filters.status')}
        value={statusValue ?? ''}
        options={statusOptions}
        onChange={(v): void => {
          onStatusChange(v === '' ? null : (v as S));
          if (v !== '') clearOthers('status');
        }}
      />
      {secondaryOptions && onSecondaryChange && (
        <SelectFilter
          label={secondaryLabel ?? t('admin.finance.filters.provider')}
          value={secondaryValue ?? ''}
          options={secondaryOptions}
          onChange={(v): void => {
            onSecondaryChange(v === '' ? null : (v as P));
            if (v !== '') clearOthers('secondary');
          }}
        />
      )}
      {methodOptions && onMethodChange && (
        <SelectFilter
          label={t('admin.finance.filters.method')}
          value={methodValue ?? ''}
          options={methodOptions}
          onChange={(v): void => {
            onMethodChange(v === '' ? null : (v as M));
            if (v !== '') clearOthers('method');
          }}
        />
      )}
    </SpaceBetween>
  );
}

/** `Select` com opção "qualquer" (`__ANY__` no Cloudscape, `''` para o chamador). */
function SelectFilter({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; labelKey: string }[];
  onChange: (v: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const cloudscapeOptions = [
    { value: ANY_VALUE, label: t('admin.finance.filters.any') },
    ...options.map((opt) => ({ value: opt.value, label: t(opt.labelKey) })),
  ];
  const selected =
    cloudscapeOptions.find((o) => o.value === (value === '' ? ANY_VALUE : value)) ?? null;
  return (
    <FormField label={label}>
      <Select
        ariaLabel={label}
        selectedOption={selected}
        options={cloudscapeOptions}
        onChange={({ detail }): void => {
          const v = detail.selectedOption.value;
          onChange(v === ANY_VALUE ? '' : (v ?? ''));
        }}
      />
    </FormField>
  );
}
